import apiClient from './apiClient';

// Response types
export interface RunningTask {
  id: string;
  taskId: string;
  status: string;
  startTime: string;
  taskName: string;
}

export interface RunningTasksResponse {
  count: number;
  tasks: RunningTask[];
}

export interface TaskLogs {
  taskId: string;
  executionCount: number;
  logs: Record<string, string>;
}

export interface TaskExecutionLogs {
  taskId: string;
  executionId: string;
  logs: string;
}

export interface Task {
  id: string;
  name: string;
  description: string;
  cronExpression: string;
  enabled: boolean;
  isEnabled?: boolean; // Backend field name for compatibility
  lastRunTime?: string;
  nextRunTime?: string;
  lastExecutionTime?: string; // Backend field name for compatibility
  nextExecutionTime?: string; // Backend field name for compatibility
}

// Tasks service for interacting with the tasks API
const tasksService = {
  /**
   * Get all tasks
   */
  getAllTasks: async (): Promise<Task[]> => {
    const response = await apiClient.get<Task[]>('/tasks');
    // Ensure consistent field mapping - the backend now returns 'enabled' field
    return response.data.map(task => ({
      ...task,
      enabled: task.enabled ?? task.isEnabled ?? false,
      lastRunTime: task.lastRunTime ?? task.lastExecutionTime,
      nextRunTime: task.nextRunTime ?? task.nextExecutionTime,
    }));
  },

  /**
   * Get all running tasks
   */
  getRunningTasks: async (): Promise<RunningTasksResponse> => {
    const response = await apiClient.get<RunningTasksResponse>('/tasks/running');
    return response.data;
  },

  /**
   * Get logs for a specific task
   */
  getTaskLogs: async (taskId: string, limit?: number): Promise<TaskLogs> => {
    const url = limit ? `/tasks/${taskId}/logs?limit=${limit}` : `/tasks/${taskId}/logs`;
    const response = await apiClient.get<TaskLogs>(url);
    return response.data;
  },

  /**
   * Get logs for a specific task execution
   */
  getTaskExecutionLogs: async (taskId: string, executionId: string): Promise<TaskExecutionLogs> => {
    const response = await apiClient.get<TaskExecutionLogs>(`/tasks/${taskId}/executions/${executionId}/logs`);
    return response.data;
  },

  /**
   * Execute a task immediately
   */
  executeTask: async (taskId: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(`/tasks/${taskId}/execute`);
    return response.data;
  },

  /**
   * Enable or disable a task
   */
  setTaskEnabled: async (taskId: string, enabled: boolean): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.patch<{ success: boolean; message: string }>(`/tasks/${taskId}/status`, { enabled });
    return response.data;
  },

  /**
   * Get statistics for all tasks
   */
  getTaskStatistics: async () => {
    const response = await apiClient.get('/tasks/stats/overview');
    return response.data;
  },

  /**
   * Get statistics for a specific task
   */
  getTaskStatisticsById: async (taskId: string) => {
    const response = await apiClient.get(`/tasks/${taskId}/stats`);
    return response.data;
  },
};

export default tasksService;
